/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import i18n from 'config/i18n';

import SelectList from 'components/SelectList';
import Button from 'components/Button';
import useIsMobile from 'utils/useIsMobile';
import { getLocationList } from './api/getLocationList';
import { cyrillicToLatinMap } from '../../utils/constants';

import styles from './style.module.scss';

const language = {
  ru: 'titleRu',
  kk: 'titleKk',
  en: 'titleEn',
  cn: 'titleEn',
};

const convertCyrillicToLatin = value => {
  return value
    .split('')
    .map(char => cyrillicToLatinMap[char] || char)
    .join('');
};

function removeDomainFromUrl(url) {
  const urlObject = new URL(url);

  return urlObject.pathname + urlObject.search + urlObject.hash;
}

const getFormatterTitle = value => {
  return value.replace(/[^a-zA-Z0-9а-яА-Я]/g, '_');
};

const getFormatLocation = data => {
  return data.map((location, index) => {
    const newLocation = {
      atsId: location.atsId,
      geonimId: location.geonimId,
      category: location.category,
      titleKk: `${location.kaz.name} ${location.kaz.typeNameShort}`,
      titleRu: `${location.rus.name} ${location.rus.typeNameShort}`,
      titleEn: convertCyrillicToLatin(
        `${location.rus.name} ${location.rus.typeNameShort}`,
      ),
    };

    if (location._links.catalog) {
      return {
        ...newLocation,
        id: `${index}${location.atsId}${location.geonimId}`,
        link: location._links.catalog.href,
        qrCode: false,
      };
    }
    if (location._links['qr-code']) {
      return {
        ...newLocation,
        link: false,
        id: location.postcode,
        qrCode: location._links['qr-code'].href,
      };
    }

    return {
      id: `${index}${location.atsId}${location.geonimId}`,
      atsId: location.atsId,
      geonimId: location.geonimId,
      category: location.category,
      titleKk: `${location.kaz.name} ${location.kaz.typeNameShort}`,
      titleRu: `${location.rus.name} ${location.rus.typeNameShort}`,
    };
  });
};

const QrCodeService = ({ onSelectAddress }) => {
  const { t } = useTranslation('qr');
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [listOfLocation, setListOfLocation] = useState([]);
  const [listOfPostcode, setListOfPostcode] = useState(null);
  const [listOfPostcodeSelected, setListOfPostcodeSelected] = useState([]);
  const [listOfLocationLink, setListOfLocationLink] = useState([]);
  const [postCodeGroup, setPostCodeGroup] = useState(null);
  const [zipArchive, setZipArchive] = useState(null);
  const [zipName, setZipName] = useState(null);

  const handleClickLocation = useCallback(async location => {
    setZipArchive(false);
    setIsLoadingList(true);
    if (location.link) {
      try {
        const res = await axios.get(removeDomainFromUrl(location.link));
        if (res.status === 204) {
          setListOfPostcode([]);
        }
        if (res.data) {
          const locationList = getFormatLocation(res.data.data);

          if (locationList.length > 0) {
            const dataWithQrCodeTrue = locationList.filter(
              item => item.qrCode !== false,
            );
            const dataWithQrCodeFalse = locationList.filter(
              item => item.qrCode === false,
            );

            if (dataWithQrCodeTrue.length) {
              setListOfPostcode(dataWithQrCodeTrue);
              setPostCodeGroup(location);
            }

            if (dataWithQrCodeFalse.length) {
              setListOfLocation(dataWithQrCodeFalse);
              setListOfLocationLink(prev => [...prev, location]);
            }
          }
        }
      } catch (error) {
        // console.log({ error });
      } finally {
        setIsLoadingList(false);
      }
    }
  }, []);

  const handleGetLocationList = useCallback(async () => {
    try {
      const res = await getLocationList();
      if (res.data) {
        const locationList = getFormatLocation(res.data.data);

        setListOfLocation(locationList);
        setListOfLocationLink(prev => prev.slice(0, -1));
      }
    } catch (error) {
      // console.log(error);
    }
  }, []);

  const handleClickBack = async () => {
    setListOfPostcode([]);
    setZipArchive(false);

    if (listOfLocationLink && listOfLocationLink.length > 1) {
      try {
        const lastLink = listOfLocationLink[listOfLocationLink.length - 2];

        const res = await axios.get(removeDomainFromUrl(lastLink.link));

        if (res.data) {
          const locationList = getFormatLocation(res.data.data);
          setListOfLocationLink(prev => prev.slice(0, -1));

          if (locationList.length > 0) {
            const dataWithQrCodeTrue = locationList.filter(
              item => item.qrCode !== false,
            );
            const dataWithQrCodeFalse = locationList.filter(
              item => item.qrCode === false,
            );

            if (dataWithQrCodeTrue.length) {
              setListOfPostcode(dataWithQrCodeTrue);
            }

            if (dataWithQrCodeFalse.length) {
              setListOfLocation(locationList);
            }
          }
        }
      } catch (error) {
        // console.error(error);
      }
    } else {
      handleGetLocationList();
    }
  };

  const handlePostCodeSelected = useCallback(postcode => {
    setZipArchive(false);
    setListOfPostcodeSelected(postcode);
  }, []);

  const fetchImage = async item => {
    const response = await axios.get(removeDomainFromUrl(item.qrCode), {
      responseType: 'blob',
    });

    return response.data;
  };

  const handleGenerateFile = async () => {
    if (onSelectAddress) {
      onSelectAddress(listOfLocationLink, listOfPostcodeSelected);
    } else {
      setIsLoading(true);
      const zip = new JSZip();
      const imagePromises = listOfPostcodeSelected.map(item =>
        fetchImage(item).then(blob => ({
          id: item.id,
          name: item[language[i18n.language]],
          blob,
        })),
      );

      const images = await Promise.all(imagePromises);

      images.forEach(({ id, name, blob }) => {
        zip.file(`${getFormatterTitle(name)}-${id}.png`, blob);
      });

      zip
        .generateAsync({ type: 'blob' })
        .then(content => {
          const name = listOfLocationLink
            .map(item => item[language[i18n.language]])
            .join('-');
          setZipArchive(content);
          setZipName(`${name}-${postCodeGroup[language[i18n.language]]}.zip`);
          setIsLoading(false);
        })
        .catch(() => {
          // console.error('Error generating zip:', error);
          setZipArchive(null);
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleDownloadArchive = () => {
    if (zipArchive) {
      saveAs(zipArchive, zipName);
    }
  };

  useEffect(() => {
    handleGetLocationList();
  }, [handleGetLocationList]);

  return (
    <div className={isMobile ? styles.mobileContainer : styles.container}>
      <h1>{!onSelectAddress && t('header')}</h1>
      <div className={styles.content}>
        <div className={styles.list}>
          <SelectList
            type="select"
            data={listOfLocation}
            defaultTitle={
              listOfLocationLink.length > 0
                ? listOfLocationLink[listOfLocationLink.length - 1]?.[
                    language[i18n.language]
                  ]
                : `${t('select')}`
            }
            onChangeSelect={handleClickLocation}
          />
          <SelectList
            type="checkbox"
            data={listOfPostcode}
            title1={t('choose')}
            title2={t('index')}
            isLoading={isLoadingList}
            onChangeCheckbox={handlePostCodeSelected}
          />
        </div>
        <div style={{ marginTop: '20px' }} />
        <div className={styles.btnGroup}>
          <div className={styles.btnGroup2}>
            <div className={styles.button1}>
              <Button
                title={t('back')}
                theme="secondary"
                isFullWidth
                isDisabled={listOfLocationLink.length === 0}
                onClick={handleClickBack}
              />
            </div>
          </div>
          <div className={styles.btnGroup2}>
            {!zipArchive && (
              <Button
                title={isLoading ? `${t('startGenerate')}` : `${t('generate')}`}
                theme="primary"
                isFullWidth
                isDisabled={!listOfPostcodeSelected.length || isLoading}
                isLoading={isLoading}
                onClick={handleGenerateFile}
              />
            )}
            {zipArchive && (
              <>
                <Button
                  title={t('download')}
                  theme="primary"
                  isFullWidth
                  isDisabled={!listOfPostcodeSelected.length}
                  isLoading={isLoading}
                  onClick={handleDownloadArchive}
                />
                <p className={styles.subtext}>
                  {t('yourZip')} {zipName}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrCodeService;
